import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SERVICE_OPTIONS, TIME_SLOTS, waLink } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  service: "",
  date: "",
  time: "",
  vehicle: "",
  name: "",
  phone: "",
  email: "",
  address: "",
  notes: "",
};

const stepMotion = {
  initial: { opacity: 0, x: 32 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -32 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const Booking = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [booking, setBooking] = useState(null);
  const [sending, setSending] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  useEffect(() => {
    if (!form.date) {
      setBookedSlots([]);
      return;
    }
    setSlotsLoading(true);
    axios
      .get(`${API}/availability`, { params: { date: form.date } })
      .then(({ data }) => {
        const booked = data.booked || [];
        setBookedSlots(booked);
        setForm((f) => (booked.includes(f.time) ? { ...f, time: "" } : f));
      })
      .catch(() => setBookedSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [form.date]);

  const canNext =
    step === 0 ? !!form.service :
    step === 1 ? !!(form.date && form.time && form.vehicle) :
    !!(form.name && form.phone && form.address);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, form);
      setBooking(data);
      toast.success(`Booking received — reference ${data.ref}`);
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-obsidian border border-white/15 rounded-sm px-4 py-3 text-sm text-white placeholder:text-mist/50 focus:outline-none focus:border-ember transition-colors";

  return (
    <section id="booking" data-testid="booking-section" className="relative bg-obsidian py-16 sm:py-24 lg:py-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <Eyebrow>Real-Time Booking</Eyebrow>
          <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
            Lock in your <span className="text-ember">driveway slot</span>
          </h2>
          <p className="mt-4 text-mist text-base max-w-lg mx-auto">
            Pick a service, choose a window, tell us where to roll. Slots update live — booked windows close automatically.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 bg-charcoal border border-white/10 rounded-sm overflow-hidden" data-testid="booking-card">
            {booking ? (
              <motion.div {...stepMotion} className="p-8 sm:p-12 text-center" data-testid="booking-confirmation">
                <CheckCircle2 className="w-16 h-16 text-ember mx-auto" strokeWidth={1.5} />
                <h3 className="mt-6 font-display font-extrabold uppercase text-3xl sm:text-4xl tracking-tight">
                  You're on the schedule
                </h3>
                <p className="mt-2 text-mist text-sm">Show this reference when we arrive:</p>
                <p className="mt-4 inline-block font-mono2 text-2xl sm:text-3xl font-bold text-ember border border-ember/40 bg-ember/10 px-6 py-3 rounded-sm tracking-widest" data-testid="booking-ref-code">
                  {booking.ref}
                </p>
                <div className="mt-8 grid grid-cols-2 gap-4 text-left max-w-md mx-auto text-sm">
                  <div><p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">Service</p><p className="mt-1 text-white">{booking.service}</p></div>
                  <div><p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">When</p><p className="mt-1 text-white">{booking.date} · {booking.time}</p></div>
                  <div><p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">Vehicle</p><p className="mt-1 text-white">{booking.vehicle}</p></div>
                  <div><p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">Where</p><p className="mt-1 text-white">{booking.address}</p></div>
                </div>
                <p className="mt-6 text-mist text-sm">A confirmation email is on its way, plus a reminder before your appointment.</p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                  <a
                    data-testid="booking-confirm-whatsapp"
                    href={waLink(`Hi GoGoAuto! Confirming booking ${booking.ref} — ${booking.service} on ${booking.date} (${booking.time}).`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-[#25D366]/60 text-[#25D366] font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-[#25D366] hover:text-obsidian transition-all duration-200"
                  >
                    <MessageCircle className="w-5 h-5" /> Confirm on WhatsApp
                  </a>
                  <button
                    data-testid="booking-new-btn"
                    onClick={() => { setBooking(null); setForm(initialForm); setStep(0); }}
                    className="font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm border border-white/20 text-mist hover:text-white hover:border-white/50 transition-all duration-200"
                  >
                    Book Another
                  </button>
                </div>
              </motion.div>
            ) : (
              <>
                <div className="flex border-b border-white/10">
                  {["Service", "Schedule", "Details"].map((label, i) => (
                    <div key={label} className={`flex-1 px-4 py-4 text-center font-display font-bold uppercase tracking-wider text-sm sm:text-base transition-colors duration-300 ${i === step ? "text-ember border-b-2 border-ember bg-ember/5" : i < step ? "text-white" : "text-mist/50"}`} data-testid={`booking-step-tab-${i}`}>
                      <span className="font-mono2 text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>{label}
                    </div>
                  ))}
                </div>

                <div className="p-6 sm:p-10 min-h-[380px]">
                  <AnimatePresence mode="wait">
                    {step === 0 && (
                      <motion.div key="s0" {...stepMotion}>
                        <p className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist mb-5">What does your vehicle need?</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {SERVICE_OPTIONS.map((s) => (
                            <button
                              key={s}
                              data-testid={`booking-service-${slug(s)}`}
                              onClick={() => set("service")(s)}
                              className={`text-left px-5 py-4 rounded-sm border font-medium text-sm transition-all duration-200 ${form.service === s ? "border-ember bg-ember/10 text-white shadow-[0_0_20px_rgba(132,220,90,0.15)]" : "border-white/15 text-mist hover:border-white/40 hover:text-white"}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {step === 1 && (
                      <motion.div key="s1" {...stepMotion} className="space-y-6">
                        <div>
                          <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Preferred date</label>
                          <input data-testid="booking-date-input" type="date" value={form.date} onChange={set("date")} min={new Date().toISOString().split("T")[0]} className={`${inputCls} [color-scheme:dark] max-w-xs`} />
                        </div>
                        <div>
                          <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist mb-2 flex items-center gap-2">
                            Time window — live availability
                            {slotsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-ember" />}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="booking-slots-grid">
                            {TIME_SLOTS.map((t) => {
                              const taken = bookedSlots.includes(t);
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  data-testid={`booking-slot-${slug(t)}`}
                                  disabled={!form.date || taken}
                                  onClick={() => set("time")(t)}
                                  className={`px-5 py-4 rounded-sm border text-sm font-medium text-left transition-all duration-200 flex items-center justify-between gap-2 ${
                                    taken
                                      ? "border-white/5 text-mist/40 line-through cursor-not-allowed bg-white/[0.02]"
                                      : form.time === t
                                        ? "border-ember bg-ember/10 text-white shadow-[0_0_20px_rgba(132,220,90,0.15)]"
                                        : !form.date
                                          ? "border-white/10 text-mist/40 cursor-not-allowed"
                                          : "border-white/15 text-mist hover:border-white/40 hover:text-white"
                                  }`}
                                >
                                  {t}
                                  {taken && <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase no-underline text-red-400/70" style={{ textDecoration: "none" }}>Booked</span>}
                                </button>
                              );
                            })}
                          </div>
                          {!form.date && <p className="text-mist/60 text-xs mt-2 font-mono2 tracking-wider">PICK A DATE FIRST TO SEE OPEN WINDOWS</p>}
                        </div>
                        <div>
                          <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Vehicle (year / make / model)</label>
                          <input data-testid="booking-vehicle-input" type="text" value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. 2019 Honda Civic" className={inputCls} />
                        </div>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div key="s2" {...stepMotion} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Your name *</label>
                            <input data-testid="booking-name-input" type="text" value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
                          </div>
                          <div>
                            <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Phone *</label>
                            <input data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(437) 000-0000" className={inputCls} />
                          </div>
                        </div>
                        <div>
                          <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Email (for confirmation)</label>
                          <input data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                        </div>
                        <div>
                          <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Service address *</label>
                          <input data-testid="booking-address-input" type="text" value={form.address} onChange={set("address")} placeholder="Street address, city" className={inputCls} />
                        </div>
                        <div>
                          <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Notes / symptoms</label>
                          <textarea data-testid="booking-notes-input" rows={3} value={form.notes} onChange={set("notes")} placeholder="Anything we should know — noises, warning lights, history…" className={inputCls} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="flex items-center justify-between px-6 sm:px-10 pb-8">
                  <button
                    data-testid="booking-back-btn"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider text-mist hover:text-white disabled:opacity-30 disabled:hover:text-mist transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  {step < 2 ? (
                    <button
                      data-testid="booking-next-btn"
                      onClick={() => canNext && setStep((s) => s + 1)}
                      disabled={!canNext}
                      className="inline-flex items-center gap-2 bg-ember text-obsidian font-display font-bold uppercase tracking-wider px-7 py-3 rounded-sm hover:bg-[#9BE873] disabled:opacity-30 disabled:hover:bg-ember transition-all duration-200"
                    >
                      Next <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      data-testid="booking-submit-btn"
                      onClick={submit}
                      disabled={!canNext || sending}
                      className="inline-flex items-center gap-2 bg-ember text-obsidian font-display font-bold uppercase tracking-wider px-7 py-3 rounded-sm hover:bg-[#9BE873] hover:shadow-[0_0_28px_rgba(132,220,90,0.4)] disabled:opacity-30 transition-all duration-200"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      {sending ? "Booking…" : "Confirm Booking"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
